// CustomVoiceInterviewTest.tsx
import React, {
    useState,
    useCallback,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
    ForwardedRef,
    useMemo,
    use,
} from "react";
import axios from "axios";
import {
    DailyAudio,
    useAppMessage,
    useAudioLevelObserver,
    useCallObject,
    useDaily,
    useDailyEvent,
    useLocalParticipant,
    useParticipantIds,
    useReceiveSettings,
    useSendSettings, // Keep if used within VideoContainer or for debugging
} from "@daily-co/daily-react";
import { DailyCall, DailyEventObjectAppMessage } from "@daily-co/daily-js";
import { VideoContainer } from "@component/video-container"; // Adjust path as needed
import type {
    VideoInterviewStatus,
    ConversationData,
    WebhookEvent,
    InterviewCompleteResult,
    VideoInterviewRef,
    TavusInteractionCallbacks,
    DailyAppMessageCallback,
    TavusAppMessageData,
    PipecatAppMessageData,
    CustomInterviewSection,
} from "@type/videoInterview"; // Adjust path as needed
import { Button } from "@component/button";
import { IssueReportModal } from "@component/issue-report-modal";
import { InterviewConfirmationModal } from "@component/interview-confirmation-modal";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@component/dropdown-menu";
import {
    MicrophoneSelector,
    SpeakerSelector,
    CameraSelector,
} from "@component/device-selectors";
import {
    Mic as MicrophoneIcon,
    Clock as ClockIcon,
    ChevronRight as ChevronRightIcon,
    AlertTriangle as AlertTriangleIcon,
    CheckCircle as CheckCircleIcon,
    VideoOff as VideoOffIcon,
    MicOff as MicOffIcon,
    Loader,
    Volume2 as SpeakerIcon,
    Camera as CameraIcon,
    CameraOffIcon,
    VideoIcon,
    StarIcon,
    TrendingUpIcon,
    LightbulbIcon,
    WifiIcon,
    HourglassIcon,
    CogIcon,
    SettingsIcon,
} from "lucide-react";
import interview, {
    createInterview,
    generateCandidateInsights,
    generatePracticeInsights,
    getInterviewStatus,
    updateInterviewStatus,
    resolveConversationId,
} from "@service/interview";
import CountdownTimer from "@component/countdown-timer";
import RetakeRequestDialog from "@component/retake-request-dialog";
import IssuesDialog from "@component/issues-dialog";
import EnhancedAudioVisualizer from "@component/enhanced-audio-visualizer";
import { SpeechDetectionProvider, useSpeechDetection } from '@context/speech_detection_context';
import type { SpeechDetectionStatus } from '@type/speechDetection';
import { getBrowserName } from "@util/string";
import BrowserCompatibilityWarning from "@component/browser-compatibility-warning";
import { formatDuration, formatHumanReadable } from "@util/date";
import { submitAssessment } from "@service/assessment";
import { CompanyPreferences } from "@type/company";
import { trackEvent } from "@util/mixpanel";
import { useParams } from "react-router-dom";
import { useSnackbar } from "@hook/use-snackbar";
import { setCookie } from "@util/cookie";
import { useCompanySubdomain } from "@context/subdomain_context";

// --- Component Props ---
export interface CustomVoiceInterviewProps {
    // Tavus & Backend Configd Config
    pipecatApiUrl: string; // Base API URL
    agentName: string; // Agent name for Pipecat
    authToken: string; // Bearer token for authentication
    s3BucketName: string;
    s3BucketRegion: string;
    awsAssumeRoleArn: string;
    fastapiURL: string;
    userId: string; // Ensure userId is always passed
    userCV?: string; // CV UUID for the user
    // Interaction Callbacks (passed down)
    tavusCallbacks?: TavusInteractionCallbacks;
    userName?: string;
    isSkipInterview?: boolean
    isLoadingSkipInterview?: boolean;
    onSkipInterview?: () => void
    // Modal Control
    onCloseModal: () => void;
    onCompletedInterview: () => void; // Callback when interview completes
    // Unique for Custom Interview
    sections: CustomInterviewSection[]; // Array of interview sections
    interviewName?: string;
    description: string | React.ReactNode; // Interview description
    maxDuration: number;
    interviewContext: string; // AI personality
    closingStatement?: string;
    interviewerPersona?: string;
    toneAndCommunication?: {
        enabled: boolean;
        selectedTones?: string[];
        additionalPreferences?: string;
    };
    interviewFlowPrinciples?: {
        enabled: boolean;
        selectedPrinciples?: string[];
        additionalGuidelines?: string;
    };
    informationToShare?: {
        enabled: boolean;
        value: string;
    };
    customGreeting?: string;    // for generalization
    aiName?: string; // AI name 
    aiLogoSrc?: string; // AI logo source
    interviewId: string
    // Test mode indicator
    isTestInterview?: boolean; // Indicates if this is a test interview
    requireSignIn?: boolean; // Whether to require user sign-in before starting the interview
    userEmail?: string; // User email for interview
    concludingStatements?: string; // Concluding statements for the interview
    job_id?: string; // Job ID for the interview, if applicable
    language?: "English" | "Vietnamese" | "FirstMove" | "Demo"; // Language for interview confirmation modal
    isPracticeInterview?: boolean; // Whether this is a practice interview
    interviewLanguage?: string;
    voice?: "male" | "female" | string;
    idleTimeoutMessage?: string; // Optional idle timeout message
    companyPreferences?: CompanyPreferences | undefined;
    isShowBrand?: boolean;
    isDisabledButtonIssue?: boolean;
    handleEndSession?: () => void;
    onReload?: () => void;
    onInterviewStatusChange?: (status: string) => void;
}


// --- Component Definition ---
const CustomVoiceInterview = forwardRef<VideoInterviewRef, CustomVoiceInterviewProps>(
    (
        {
            // Destructure props
            interviewId,
            pipecatApiUrl,
            agentName,
            authToken,
            interviewContext,
            closingStatement,
            interviewerPersona,
            toneAndCommunication,
            interviewFlowPrinciples,
            informationToShare,
            s3BucketName,
            s3BucketRegion,
            awsAssumeRoleArn,
            fastapiURL,
            interviewName,
            customGreeting,
            tavusCallbacks,
            onCloseModal,
            userId,
            userCV,
            userName,
            isSkipInterview = false,
            isLoadingSkipInterview = false,
            onSkipInterview,
            onCompletedInterview,
            sections,
            description,
            maxDuration,
            aiName = "ourteam AI",
            aiLogoSrc,
            isTestInterview = false,
            requireSignIn = false, // Whether to require user sign-in before starting the interview
            userEmail = "",
            concludingStatements = "Thank you for your time and thoughtful responses. Have a great day!",
            job_id = null,
            language = "English",
            isPracticeInterview = false, // Whether this is a practice interview,
            interviewLanguage = "en-US",
            voice = "male",
            idleTimeoutMessage,
            companyPreferences = undefined,
            isShowBrand = false,
            isDisabledButtonIssue = false,
            handleEndSession,
            onReload,
            onInterviewStatusChange = () => { }
        },
        ref: ForwardedRef<VideoInterviewRef>
    ) => {
        const { jobid, companyname } = useParams() || null;
        const snackbar = useSnackbar();
        const [isLoadingCompanyInfo, companyInfo] = useCompanySubdomain();

        const isMalay = interviewLanguage === "Malay";
        const isIndonesian = interviewLanguage === "id-ID";

        const t = (en: string, id: string, ms: string) => {
            if (isMalay) return ms;
            if (isIndonesian) return id;
            return en;
        };
        // --- State ---
        const [status, setStatus] = useState<VideoInterviewStatus>("idle");
        const [conversationId, setConversationId] = useState<string | null>(null);
        const [conversationUrl, setConversationUrl] = useState<string | null>(null);
        const [conversationToken, setConversationToken] = useState<string | null>(null);
        const [isConversationEnded, setIsConversationEnded] = useState<boolean>(false);

        const [apiErrorMessage, setApiErrorMessage] = useState<string>("");
        const [internalErrorMessage, setInternalErrorMessage] = useState<string | React.ReactNode>("");
        const [countdownSeconds, setCountdownSeconds] = useState<number>(30); // Add countdown state

        // FirstMove color scheme detection
        const isFirstMoveInterview = aiName?.toLowerCase().includes('first move') || false;
        const primaryColor = isFirstMoveInterview ? 'black' : 'blue';
        const primaryColorClasses = isFirstMoveInterview
            ? {
                bg: 'bg-black',
                bgHover: 'hover:bg-gray-800',
                bgLight: 'bg-gray-50',
                bgLightDark: 'dark:bg-gray-900/30',
                border: 'border-gray-100',
                borderDark: 'dark:border-gray-800',
                text: 'text-gray-600',
                textDark: 'dark:text-gray-400',
                gradient: 'bg-gradient-to-r from-gray-400 to-gray-500',
                ring: 'border-gray-400',
                ringLight: 'border-gray-300',
                ringLighter: 'border-gray-200'
            }
            : {
                bg: 'bg-blue-600',
                bgHover: 'hover:bg-blue-700',
                bgLight: 'bg-blue-50',
                bgLightDark: 'dark:bg-blue-900/30',
                border: 'border-blue-100',
                borderDark: 'dark:border-blue-800',
                text: 'text-blue-600',
                textDark: 'dark:text-blue-400',
                gradient: 'bg-gradient-to-r from-blue-400 to-blue-500',
                ring: 'border-blue-400',
                ringLight: 'border-blue-300',
                ringLighter: 'border-blue-200'
            };

        // Media State
        const [micAccessGranted, setMicAccessGranted] = useState(false);
        const [cameraAccessGranted, setCameraAccessGranted] = useState(false);
        const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
        const [mediaError, setMediaError] = useState<string | null>(null);

        // UI State
        const [showIssueModal, setShowIssueModal] = useState(false); // Keep if IssueReportModal is used separately
        const [showConfirmationModal, setShowConfirmationModal] = useState(false);
        const [showVideoUI, setShowVideoUI] = useState(false); // Determines if waiting room or main interview UI is shown
        const [AiJoined, setAiJoined] = useState(false); // AI participant joined Daily call 
        const [isLoadingJoin, setLoadingJoin] = useState<boolean>(false);
        const [dailyJoined, setDailyJoined] = useState(false); // Local participant joined Daily call
        const [isConcluding, setIsConcluding] = useState(false);

        // Device Management State
        const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([]);
        const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
        const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
        const [selectedAudioInputId, setSelectedAudioInputId] = useState<string>("");
        const [selectedAudioOutputId, setSelectedAudioOutputId] = useState<string>("");
        const [selectedVideoId, setSelectedVideoId] = useState<string>("");

        // Interview Flow State
        const [blockInterview, setBlockInterview] = useState<boolean>(false); // Blocks starting if status check fails (e.g., refresh)
        const [interviewExpired, setInterviewExpired] = useState<boolean>(false); // Countdown timed out
        const [isRetakeDialogOpen, setIsRetakeDialogOpen] = useState<boolean>(false);
        const [isIssuesDialogOpen, setIsIssuesDialogOpen] = useState<boolean>(false);
        const [requestSubmitted, setRequestSubmitted] = useState<boolean>(false); // Retake/Issue submitted
        const [requestType, setRequestType] = useState<"retake" | "issue" | null>(null);
        const [countdownActive, setCountdownActive] = useState<boolean>(false); // Timer started
        const [interviewStatus, setInterviewStatus] = useState<string | null>(null); // Initial status
        const [interviewStep, setInterviewStep] = useState<number>(0);
        const [isRecordingStarting, setIsRecordingStarting] = useState<boolean>(false); // Recording state

        // Add interview duration tracking
        const [interviewStartTime, setInterviewStartTime] = useState<number>(0);

        // --- Refs ---
        const videoRef = useRef<HTMLVideoElement>(null);
        const audioRef = useRef<HTMLAudioElement>(null); // Ref for DailyAudio
        const conversationCreatedRef = useRef<boolean>(false); // Prevent multiple creation attempts
        const mediaStreamRef = useRef<MediaStream | null>(null); // Ref to track mediaStream for cleanup
        const callObjectRef = useRef<ReturnType<typeof useDaily>>(null); // Ref to track callObject for cleanup
        const [isAiSpeaking, setIsAiSpeaking] = useState(false);
        // const [isUserSpeaking, setIsUserSpeaking] = useState(false); // Track if user is speaking

        // Keep mediaStreamRef in sync with mediaStream state
        useEffect(() => {
            mediaStreamRef.current = mediaStream;
        }, [mediaStream]);


        // Long pause detection
        const [lastUtteranceTime, setLastUtteranceTime] = useState<number | null>(null);
        const [conversationStarted, setConversationStarted] = useState(false);
        const longPauseTimerRef = useRef<NodeJS.Timeout | null>(null);

        // Enhanced pause detection state
        const [pauseState, setPauseState] = useState<0 | 1 | 2>(0); // 'none' | 'first-warning' | 'second-warning'
        const pauseTimerRef = useRef<NodeJS.Timeout | null>(null);
        const lastActivityTimestampRef = useRef<number>(Date.now());

        const callObject = useDaily();

        // Keep callObjectRef in sync with callObject
        useEffect(() => {
            callObjectRef.current = callObject;
        }, [callObject]);

        const localParticipant = useLocalParticipant();
        const remoteParticipantIds = useParticipantIds({ filter: "remote" }); // <-- Get remote participants

        const interviewStart = useRef<number | null>(null); // Track when the interview 

        const [isBrowserSupported, setIsBrowserSupported] = useState<boolean>(true);

        const interviewContexts = React.useMemo<string[]>(() => {
            console.log("Generating interview context instructions for sections:", JSON.stringify(sections));
            return sections.map((section) => (`Current Section Instructions:\n${section?.instruction}\n\n${(section.questions || []).length > 0 ? `### MANDATORY QUESTIONS ###\n\nYou MUST ask these questions in order (but make it natural):\n\n${(section?.questions || []).map((q, idx) => { return `${idx + 1}. ${q.text}\n${(q?.followUpInstructions || []).length > 0 ? `Follow up instructions: ${(q?.followUpInstructions || []).join('; ')}` : ''}`; }).join('\n')}` : ""}`))
        }, [sections]);

        const detectBrowser = () => {
            const { name } = getBrowserName();

            console.log("Browser detection:", {
                name,
                isSupported: name === "Chrome" || name === "Firefox" || name === "Safari" || name === "Edge" || name === "Mozilla"
            });

            return name === "Chrome" || name === "Firefox" || name === "Safari" || name === "Edge" || name === "Mozilla";
        };

        // Check browser compatibility on mount
        useEffect(() => {
            setIsBrowserSupported(detectBrowser());
        }, []);

        // Identify the AI participant (assuming it's the first remote participant)
        const aiParticipantId = useMemo(() => {
            // You might need a more robust way to identify the AI if multiple remotes are possible
            return remoteParticipantIds.length > 0 ? remoteParticipantIds[0] : null;
        }, [remoteParticipantIds]);

        // --- Constants ---
        const timeoutMinutes = 3; // Countdown duration
        const { receiveSettings, updateReceiveSettings } = useReceiveSettings()
        const { sendSettings, updateSendSettings } = useSendSettings()

        // -- Talking Indicator --
        const { isTalking, speech_status, error, startListening, stopListening, audioStatus } = useSpeechDetection();

        useEffect(() => {
            console.log("Current Speech Status:", speech_status); // Log status changes
            if (error) {
                console.error("Speech Detection Error:", error); // Log errors
            }
        }, [speech_status, error]);

        useEffect(() => {
            let intervalId: NodeJS.Timeout | null = null;

            if (isConcluding) {
                // Start the countdown timer
                intervalId = setInterval(() => {
                    setCountdownSeconds((prevSeconds) => {
                        if (prevSeconds <= 1) {
                            // If countdown reaches 0 or less, clear the interval
                            // endConversation('end')
                            // setStatus('completed');
                            console.log("timeout")
                            endConversation('end')
                            if (!isTestInterview) {
                                updateInterviewStatus({ new_status: "Complete", conversation_id: conversationId as string }, interviewId === import.meta.env.VITE_FIRST_MOVE_INTERVIEW_ID ? import.meta.env.VITE_FIRST_MOVE_API : undefined).then(() => {
                                    if (onInterviewStatusChange) {
                                        onInterviewStatusChange("Complete");
                                    }
                                    submitAssessment({
                                        assessment_id: interviewId,
                                        answers: [{ id: "conversation_id", content: conversationId as string }],
                                        job_id: job_id as string,
                                        submission_type: "Interview"
                                    }).then(() => {
                                        console.log("Assessment submitted successfully.");
                                    }).catch((err) => {
                                        console.error("Failed to submit assessment:", err);
                                        setApiErrorMessage("Failed to submit your responses. Please try again later.");
                                    });
                                    onCompletedInterview();
                                })
                            }
                            setStatus('completed');
                            if (intervalId) {
                                clearInterval(intervalId);
                            }
                            return 0; // Keep it at 0
                        }
                        return prevSeconds - 1; // Decrement
                    });
                }, 1000); // Run every 1 second
            } else {
                // If not concluding, ensure timer is cleared (cleanup also does this)
                if (intervalId) {
                    clearInterval(intervalId);
                }
                // Optionally reset countdownSeconds when concluding stops if needed
                // setCountdownSeconds(30); // Reset to initial value
            }

            // Cleanup function: Clear the interval when the component unmounts
            // or when isConcluding changes before the interval finishes
            return () => {
                if (intervalId) {
                    clearInterval(intervalId);
                }
            };
        }, [isConcluding]); // Re-run effect if isConcluding changes

        useEffect(() => {
            // Ensure callObject and the AI participant ID are available
            if (callObject && aiParticipantId) {
                console.log(
                    `VideoContainer: Requesting low quality video layer for AI participant (${aiParticipantId})`
                );
                updateReceiveSettings({
                    // Target the specific AI participant using their session ID
                    [aiParticipantId]: {
                        video: {
                            // Request the lowest quality video layer (0)
                            // 0 = low, 1 = medium, 2 = high (if available)
                            // This relies on the sender (AI) using simulcast.
                            layer: 0,

                            // Optional: You could also set 'subscribe: true/false' here
                            // if you wanted to completely turn the video track on/off,
                            // but 'layer: 0' is better for reducing quality while keeping video.
                        },
                        // You can also set settings for 'audio' or 'screenVideo' here if needed
                    },
                    // You could set global defaults using '*': {} , but we want specific control
                });
                console.log(
                    `VideoContainer: Requesting low quality video layer for user participant (${aiParticipantId})`
                );
                updateSendSettings({
                    video: 'bandwidth-optimized',
                }

                )
            }
        }, [callObject, aiParticipantId]); // Re-run this effect if the callObject or AI participant ID changes


        // --- Callbacks & Handlers ---

        const handleAudioPlayFailed = useCallback((e: any) => {
            console.error(
                `Failed to play ${e.type} audio for ${e.sessionId || "unknown participant"
                }.`,
                e
            );
            // Optional: Update UI to show audio playback error
            setInternalErrorMessage(
                "Audio playback failed. Check speaker settings or browser permissions."
            );
        }, []);

        const setupMediaStream = useCallback(
            async (
                audioDeviceId: string = selectedAudioInputId, // Default to current selection
                videoDeviceId: string = selectedVideoId // Default to current selection
            ) => {
                console.log(
                    `Attempting to setup media stream with audio: ${audioDeviceId}, video: ${videoDeviceId}`
                );

                // Define constraints variable *before* the try block
                let constraints: MediaStreamConstraints = {}; // Initialize

                // Stop any existing tracks before requesting new ones
                if (mediaStream) {
                    console.log("Stopping previous media tracks");
                    mediaStream.getTracks().forEach((track) => {
                        track.stop();
                        console.log(`Stopped ${track.kind} track: ${track.label}`);
                    });
                    setMediaStream(null); // Clear the state immediately
                    if (videoRef.current) {
                        videoRef.current.srcObject = null; // Clear video element source
                    }
                }

                // Check if device IDs are actually available (might be empty initially)
                if (!audioDeviceId && audioInputDevices.length > 0) {
                    console.warn("No specific audio input ID, but devices exist. Will use default.");
                    audioDeviceId = audioInputDevices[0].deviceId; // Fallback might be needed
                }
                if (!videoDeviceId && videoDevices.length > 0) {
                    console.warn("No specific video input ID, but devices exist. Will use default.");
                    videoDeviceId = videoDevices[0].deviceId; // Fallback might be needed
                }

                try {
                    // Assign constraints inside the try block (or re-assign if needed)
                    constraints = {
                        video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
                    };

                    console.log("Requesting media with constraints:", constraints);
                    const stream = await navigator.mediaDevices.getUserMedia(constraints);
                    console.log("Stream obtained successfully:", stream.id);
                    console.log("Video tracks:", stream.getVideoTracks().map((t) => `${t.label} (${t.id})`));

                    setMediaStream(stream); // Update state
                    setCameraAccessGranted(true);
                    setMediaError(null);

                    // Attach stream to video element
                    if (videoRef.current) {
                        console.log("Attaching stream to video element");
                        videoRef.current.srcObject = stream;
                        await videoRef.current.play().catch((err) => {
                            console.error("Error playing video preview:", err);
                            // It might still work, but log the error. Autoplay might be blocked.
                        });
                    } else {
                        console.warn("Video ref is not available for preview.");
                    }

                    // Apply audio output selection if supported
                    if (
                        selectedAudioOutputId &&
                        audioRef.current && // Use the DailyAudio ref
                        "setSinkId" in HTMLMediaElement.prototype
                    ) {
                        try {
                            console.log("Setting audio output device:", selectedAudioOutputId);
                            if (typeof (audioRef.current as any).setSinkId === 'function') {
                                await (audioRef.current as any).setSinkId(selectedAudioOutputId);
                            } else {
                                console.warn('setSinkId not supported on this audio element');
                            }
                        } catch (err) {
                            console.error("Error setting audio output device:", err);
                            setInternalErrorMessage("Failed to set speaker device.");
                        }
                    }
                    return stream; // Return the stream for potential immediate use elsewhere
                } catch (err) {
                    console.error("Error accessing media devices:", err);
                    let errorMessage = "Could not access camera or microphone.";
                    if (err instanceof Error) {
                        if (err.name === "NotAllowedError") {
                            errorMessage =
                                "Permission denied. Please grant access to your camera and microphone in browser settings.";
                        } else if (err.name === "NotFoundError") {
                            errorMessage =
                                "No camera or microphone found. Please check connections.";
                        } else if (err.name === "OverconstrainedError") {
                            errorMessage =
                                "Selected device unavailable. Choose another or ensure it's not in use.";
                            // Reset selection if the specific device caused the error
                            // Now 'constraints' is accessible here
                            if ((err as any).constraint === 'deviceId' && constraints) {
                                if (constraints.audio && typeof constraints.audio === 'object' && constraints.audio.deviceId === audioDeviceId) {
                                    console.warn("Resetting failed audio input selection due to OverconstrainedError");
                                    setSelectedAudioInputId(''); // Reset to default
                                }
                                if (constraints.video && typeof constraints.video === 'object' && constraints.video.deviceId === videoDeviceId) {
                                    console.warn("Resetting failed video input selection due to OverconstrainedError");
                                    setSelectedVideoId(''); // Reset to default
                                }
                            }
                        } else {
                            errorMessage = `Media Error: ${err.message}`;
                        }
                    }
                    setMediaError(errorMessage);
                    setCameraAccessGranted(false);
                    setMediaStream(null);
                    if (videoRef.current) {
                        videoRef.current.srcObject = null;
                    }
                }
            },
            [mediaStream, selectedAudioInputId, selectedVideoId, selectedAudioOutputId, audioInputDevices, videoDevices] // Dependencies needed for correct closure
        );

        // Effect for Initial Device Enumeration and Stream Setup (Waiting Room Only)
        useEffect(() => {
            // Only run this setup when in the waiting room UI
            if (showVideoUI) return;

            let isMounted = true; // Flag to prevent state updates after unmount
            // Store video ref at mount time for cleanup (to satisfy React lint rules)
            const currentVideoRefAtMount = videoRef.current;

            const enumerateAndSetup = async () => {
                console.log("Effect: Enumerating devices and setting up initial stream...");
                try {
                    // Request dummy stream first to ensure permissions are granted *before* enumeration
                    // This helps get accurate device labels.
                    // Track all temp streams to ensure proper cleanup
                    const tempStreamsToCleanup: MediaStream[] = [];

                    try {
                        const videoTempStream = await navigator.mediaDevices.getUserMedia({ video: true });
                        tempStreamsToCleanup.push(videoTempStream);
                        setCameraAccessGranted(true);
                        console.warn("Video permission granted.");
                    } catch (permError) {
                        console.error("Initial permission request for camera failed:", permError);
                        // Set error state based on permission denial
                        if (permError instanceof Error && permError.name === 'NotAllowedError') {
                            setMediaError("Permission denied. Please grant access to camera.");
                        } else {
                            setMediaError("Could not access camera. Check connections/permissions.");
                        }
                        setCameraAccessGranted(false);
                        // Still try to enumerate, might get devices without labels
                    }

                    try {
                        const audioTempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                        tempStreamsToCleanup.push(audioTempStream);
                        setMicAccessGranted(true);
                        console.warn("Mic permission granted.");
                    } catch (permError) {
                        console.error("Initial permission request for mic failed:", permError);
                        // Set error state based on permission denial
                        if (permError instanceof Error && permError.name === 'NotAllowedError') {
                            setMediaError("Permission denied. Please grant access to microphone.");
                        } else {
                            setMediaError("Could not access microphone. Check connections/permissions.");
                        }

                        setMicAccessGranted(false);
                        // Still try to enumerate, might get devices without labels
                    }


                    // Now enumerate devices
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    console.log("Enumerated devices:", devices);

                    const audioInputs = devices.filter(
                        (d) => d.kind === "audioinput" && d.deviceId !== "default" && d.deviceId !== 'communications' && d.deviceId !== ""
                    );
                    const audioOutputs = devices.filter((d) => d.kind === "audiooutput" && d.deviceId !== "");
                    const videoInputs = devices.filter((d) => d.kind === "videoinput" && d.deviceId !== "");

                    if (!isMounted) return; // Check before setting state

                    setAudioInputDevices(audioInputs);
                    setAudioOutputDevices(audioOutputs);
                    setVideoDevices(videoInputs);

                    // Stop ALL temporary streams *after* enumeration
                    tempStreamsToCleanup.forEach(stream => {
                        stream.getTracks().forEach(track => {
                            console.log(`Stopping temp stream track: ${track.kind} (${track.label})`);
                            track.stop();
                        });
                    });

                    // Determine initial device selections (use first available if no selection yet)
                    const initialAudioInputId = selectedAudioInputId || (audioInputs.length > 0 ? audioInputs[0].deviceId : '');
                    const initialVideoId = selectedVideoId || (videoInputs.length > 0 ? videoInputs[0].deviceId : '');
                    const initialAudioOutputId = selectedAudioOutputId || (audioOutputs.length > 0 ? audioOutputs[0].deviceId : '');

                    // Update state only if changed
                    if (initialAudioInputId !== selectedAudioInputId) setSelectedAudioInputId(initialAudioInputId);
                    if (initialVideoId !== selectedVideoId) setSelectedVideoId(initialVideoId);
                    if (initialAudioOutputId !== selectedAudioOutputId) setSelectedAudioOutputId(initialAudioOutputId);


                    // Setup the actual media stream *if* permissions were likely granted (or if devices exist)
                    if (tempStreamsToCleanup.length > 0 || (audioInputs.length > 0 && videoInputs.length > 0)) {
                        await setupMediaStream(initialAudioInputId, initialVideoId);
                    }


                    // Add device change listener
                    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

                } catch (error) {
                    console.error("Error during initial device setup:", error);
                    if (isMounted) {
                        setMediaError(
                            "Error accessing media devices. Check permissions/connections."
                        );
                    }
                }
            };

            // Handler for device changes *after* initial setup
            const handleDeviceChange = async () => {
                if (!isMounted || showVideoUI) return; // Don't run if not mounted or in main interview
                console.log("Device change detected. Re-enumerating...");

                // Re-enumerate to get updated list
                const devices = await navigator.mediaDevices.enumerateDevices();
                const audioInputs = devices.filter(
                    (d) => d.kind === "audioinput" && d.deviceId !== "default" && d.deviceId !== 'communications'
                );
                const audioOutputs = devices.filter((d) => d.kind === "audiooutput");
                const videoInputs = devices.filter((d) => d.kind === "videoinput");

                if (!isMounted) return;

                setAudioInputDevices(audioInputs);
                setAudioOutputDevices(audioOutputs);
                setVideoDevices(videoInputs);

                // Check if the *currently selected* devices still exist
                const audioInputStillExists = audioInputs.some(
                    (d) => d.deviceId === selectedAudioInputId
                );
                const videoStillExists = videoInputs.some(
                    (d) => d.deviceId === selectedVideoId
                );

                let newAudioId = selectedAudioInputId;
                let newVideoId = selectedVideoId;
                let streamNeedsUpdate = false;

                if (!audioInputStillExists && audioInputs.length > 0) {
                    console.warn("Selected audio input lost. Selecting new default.");
                    newAudioId = audioInputs[0].deviceId;
                    setSelectedAudioInputId(newAudioId); // Update state
                    streamNeedsUpdate = true;
                } else if (!audioInputStillExists && audioInputs.length === 0) {
                    console.error("All audio input devices lost.");
                    setMicAccessGranted(false);
                    setMediaError("Microphone disconnected.");
                    setSelectedAudioInputId('');
                    streamNeedsUpdate = true; // Attempt to stop the old stream
                }

                if (!videoStillExists && videoInputs.length > 0) {
                    console.warn("Selected video input lost. Selecting new default.");
                    newVideoId = videoInputs[0].deviceId;
                    setSelectedVideoId(newVideoId); // Update state
                    streamNeedsUpdate = true;
                } else if (!videoStillExists && videoInputs.length === 0) {
                    console.error("All video input devices lost.");
                    setCameraAccessGranted(false);
                    setMediaError("Camera disconnected.");
                    setSelectedVideoId('');
                    streamNeedsUpdate = true; // Attempt to stop the old stream
                }

                // Check audio output
                const audioOutputStillExists = audioOutputs.some(
                    (d) => d.deviceId === selectedAudioOutputId
                );
                if (!audioOutputStillExists && audioOutputs.length > 0) {
                    console.warn("Selected audio output lost. Selecting new default.");
                    setSelectedAudioOutputId(audioOutputs[0].deviceId);
                    // No stream update needed for output, just apply sinkId if possible later
                } else if (!audioOutputStillExists && audioOutputs.length === 0) {
                    console.warn("All audio output devices lost.");
                    setSelectedAudioOutputId('');
                }


                if (streamNeedsUpdate) {
                    console.log("Device change necessitates stream update.");
                    await setupMediaStream(newAudioId, newVideoId);
                }
            };


            enumerateAndSetup();

            // Cleanup function
            return () => {
                isMounted = false;
                console.log("Effect Cleanup: Media setup", mediaStreamRef.current);
                navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);

                // Stop media tracks on cleanup using ref (to get the latest value)
                if (mediaStreamRef.current) {
                    console.log("Cleaning up media tracks from effect");
                    mediaStreamRef.current.getTracks().forEach((track) => {
                        console.log(`Stopping track: ${track.kind} (${track.label})`);
                        track.stop();
                    });
                    mediaStreamRef.current = null;
                }
                // Clear video source using the ref captured at mount
                const videoRefToClean = currentVideoRefAtMount || videoRef.current;
                if (videoRefToClean) {
                    console.log("Clearing video source on cleanup");
                    videoRefToClean.srcObject = null;
                }
            };
        }, []); // Rerun only if we switch between waiting room and main UI

        // Local speech detection
        useEffect(() => {
            // Try starting speech detection once mic access is confirmed
            if (micAccessGranted) {
                console.log("Attempting to start local speech detection...");
                startListening().catch(err => {
                    // Handle potential errors from startListening itself if needed
                    console.error("Error calling startListening:", err);
                });
            }

            // Cleanup: Stop speech detection when component unmounts or micAccessGranted becomes false
            return () => {
                console.log("Speech detection effect cleanup: stopping listening");
                stopListening();
            };
        }, [micAccessGranted, startListening, stopListening]);

        // Device Selection Handlers
        const handleMicrophoneSelect = useCallback((device: MediaDeviceInfo) => {
            console.log(`Microphone selected: ${device.label} (${device.deviceId})`);
            setSelectedAudioInputId(device.deviceId);

            if (callObject && callObject.meetingState() === 'joined-meeting') {
                console.log("Applying microphone change to Daily call:", device.deviceId);
                callObject.setInputDevicesAsync({ audioDeviceId: device.deviceId })
                    .catch(err => console.error("Error setting Daily audio input device:", err));

                setupMediaStream(device.deviceId, selectedVideoId);
            } else {
                // Still update local preview if not in main call UI or Daily not ready
                console.log("Applying microphone change to local preview.");
                // Ensure selectedVideoId is current when calling setupMediaStream
                setupMediaStream(device.deviceId, selectedVideoId);
            }
        }, [selectedVideoId, setupMediaStream, callObject]);

        const handleSpeakerSelect = useCallback((device: MediaDeviceInfo) => {
            console.log(`Speaker selected: ${device.label} (${device.deviceId})`);
            setSelectedAudioOutputId(device.deviceId);

            if (callObject && callObject.meetingState() === 'joined-meeting') {
                console.log("Applying speaker change to Daily call:", device.deviceId);
                if ('setOutputDeviceAsync' in callObject) {
                    callObject.setOutputDeviceAsync({ outputDeviceId: device.deviceId })
                        .catch(err => console.error("Error setting Daily audio output device:", err));
                } else {
                    console.warn("callObject.setOutputDeviceAsync is not available. Speaker change might not apply to Daily call.");
                }
            } else {
                // For waiting room, apply to DailyAudio element
                if (audioRef.current && "setSinkId" in HTMLMediaElement.prototype) {
                    console.log("Applying speaker change to local audio element (DailyAudio).");
                    if (typeof (audioRef.current as any).setSinkId === 'function') {
                        (audioRef.current as any).setSinkId(device.deviceId).catch((err: any) => {
                            console.error("Error setting audio output device on selection (local):", err);
                        });
                    } else {
                        console.warn('setSinkId not supported on this audio element');
                    }
                }
            }
        }, [callObject]);

        const handleCameraSelect = useCallback((device: MediaDeviceInfo) => {
            console.log(`Camera selected: ${device.label} (${device.deviceId})`);
            setSelectedVideoId(device.deviceId);

            if (callObject && callObject.meetingState() === 'joined-meeting') {
                console.log("Applying camera change to Daily call:", device.deviceId);
                callObject.setInputDevicesAsync({ videoDeviceId: device.deviceId })
                    .catch(err => console.error("Error setting Daily video input device:", err));
                setupMediaStream(selectedAudioInputId, device.deviceId);
            } else {
                // Still update local preview
                console.log("Applying camera change to local preview.");
                // Ensure selectedAudioInputId is current when calling setupMediaStream
                setupMediaStream(selectedAudioInputId, device.deviceId);
            }
        }, [selectedAudioInputId, setupMediaStream, callObject]);


        // Check Interview Status (e.g., prevent joining if already completed/blocked)
        const checkInterviewStatus = useCallback(async () => {
            console.log("Checking interview status...");
            try {
                if (requireSignIn) {
                    const res = await getInterviewStatus(interviewId); // Assuming this uses userId implicitly or pass it
                    console.log("Interview Status Check Response:", res);
                    setInterviewStatus(res?.data?.status || null);
                    if (res?.data?.status === "Pending Interview") {
                        setBlockInterview(false);
                    } else {
                        // Handle specific statuses with custom messages
                        console.warn(`Interview status is not 'Pending Interview': ${res?.data?.status}`);
                        setBlockInterview(true);

                        // Set specific error messages based on status
                        if (res?.data?.status === "None") {
                            setInternalErrorMessage(<>
                                This interview session has expired. You did not join within<br />the allocated time.
                            </>);
                        } else if (res?.data?.status === "Interviewer Ready") {
                            setInternalErrorMessage("It looks like you refreshed the page, which ended the session. \nPlease request to retake the interview.");
                        } else {
                            // Default message for other statuses
                            setInternalErrorMessage(`Interview access blocked (Status: ${res?.data?.status}). You may need to request a retake.`);
                        }
                    }
                } else {
                    setBlockInterview(false);
                    setInterviewStatus("Pending Interview"); // Default for non-authenticated interviews
                }
            } catch (error: any) {
                console.error("Failed to get interview status:", error);
                setBlockInterview(true); // Block if status check fails
                setInternalErrorMessage("Could not verify interview status. Please try refreshing or contact support.");
            }
        }, []);

        useEffect(() => {
            // Check status once when the component mounts
            checkInterviewStatus();
        }, []);


        // Create Conversation via pipecat cloud
        const createConversation = useCallback(async () => {
            // Prevent creation if blocked, already created, or in wrong state
            if (blockInterview || conversationCreatedRef.current || (status !== 'idle' && status !== 'error')) {
                console.warn(`Skipping conversation creation. Blocked: ${blockInterview}, Created: ${conversationCreatedRef.current}, Status: ${status}`);
                return;
            }

            conversationCreatedRef.current = true; // Mark as attempting creation
            setStatus('connecting');
            setApiErrorMessage('');
            setInternalErrorMessage(''); // Clear previous errors

            console.log("API: Creating Pipecat conversation...");
            try {
                // Construct the request URL with agent name
                const requestUrl = `${pipecatApiUrl}/v1/public/${agentName}/start`;

                // Prepare request body according to Pipecat's format
                // Generate a UUID for the conversation
                let newConversationId = crypto.randomUUID();

                // Only create interview in backend if NOT a test interview
                if (!isTestInterview) {
                    console.log("Creating backend interview for non-test mode", interviewId, interviewName, newConversationId);
                    if (interviewId === import.meta.env.VITE_FIRST_MOVE_INTERVIEW_ID) {
                        console.log("Creating backend interview for First Move interview");
                        console.log("User Email:", userEmail);
                        const createResponse = await createInterview({ conversation_id: newConversationId, interview: interviewName, assessment_id: interviewId, browser_details: `${getBrowserName().name} (${getBrowserName().version})`, email: userEmail, job_id: job_id }, import.meta.env.VITE_FIRST_MOVE_API);
                        // The BE owns the conversation_id — adopt whatever it returns,
                        // on 201 and 202 alike. Keeping our local id makes every later
                        // call 404 and silently loses the interview (#3460).
                        newConversationId = resolveConversationId(createResponse, newConversationId);
                        console.log("Backend create interview response:", createResponse);
                    }
                    else {
                        const createResponse = await createInterview({ conversation_id: newConversationId, interview: interviewName, assessment_id: interviewId, browser_details: `${getBrowserName().name} (${getBrowserName().version})`, job_id: job_id });
                        // The BE owns the conversation_id — adopt whatever it returns,
                        // on 201 and 202 alike. Keeping our local id makes every later
                        // call 404 and silently loses the interview (#3460).
                        newConversationId = resolveConversationId(createResponse, newConversationId);
                        console.log("Backend create interview response:", createResponse);

                    }
                } else {
                    console.log("Test interview - skipping backend interview creation");
                }

                const requestBody = {
                    createDailyRoom: true,
                    dailyRoomProperties: {
                        // enable_recordings: true, // Enable recordings
                        max_participants: 2, // Only AI and user
                        exp: Math.floor(Date.now() / 1000) + 1500, // Room expires in 25 minutes (25*60 = 1500 seconds)
                        eject_at_room_exp: true, // Eject participants when room expires
                        enable_recording: isTestInterview ? false : "cloud", // Don't record in test mode
                        start_video_off: false
                    },
                    body: {
                        // Custom properties for the agent
                        interviewName: interviewName || `Interview - ${userId} - ${new Date().toISOString()}`,
                        interviewContext: interviewContext,
                        closingStatement,
                        interviewerPersona,
                        toneAndCommunication,
                        interviewFlowPrinciples,
                        informationToShare,
                        aiName: aiName,
                        userName: userName,
                        customGreeting: customGreeting,
                        userId: userId,
                        userCV: userCV ? userCV : undefined,
                        fastapiURL: fastapiURL,
                        conversationId: newConversationId, // Use the generated UUID
                        s3BucketName: s3BucketName,
                        s3BucketRegion: s3BucketRegion,
                        awsAssumeRoleArn: awsAssumeRoleArn,
                        isTestInterview: isTestInterview, // Pass the test flag to the agent
                        concludingStatements: concludingStatements, // Pass the concluding statements
                        language: interviewLanguage, // Pass the language setting
                        voice: voice,
                        idleTimeoutMessage: idleTimeoutMessage || "It seems like you're busy right now. Have a nice day!", // Pass the idle timeout message
                        isDemo: language === "Demo",
                    }
                };

                const response = await axios.post(
                    requestUrl,
                    requestBody,
                    {
                        headers: {
                            'Authorization': `Bearer ${authToken}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                console.log("API: Pipecat conversation created:", response.data);
                setCookie('interview_conversation_id', newConversationId, 1); // 1 day expiry

                // Update state with Pipecat's response format
                setConversationId(newConversationId); // Use token as ID if needed
                setConversationUrl(response.data.dailyRoom); // Pipecat returns dailyRoom as the URL
                setConversationToken(response.data.dailyToken); // Token for Daily join
                setStatus('active'); // Ready for Daily join
            } catch (error: any) {
                console.error('API: Failed to create Pipecat conversation:', error);
                conversationCreatedRef.current = false; // Allow retry if needed
                setApiErrorMessage(error.response?.data?.message || 'Failed to create Pipecat conversation. Please check configuration or network.');
                setStatus('error');
            }
        }, [
            blockInterview, status, pipecatApiUrl, agentName,
            interviewName, interviewContext, customGreeting, fastapiURL,
            s3BucketName, s3BucketRegion, awsAssumeRoleArn, authToken, userId, isTestInterview, idleTimeoutMessage
        ]);

        // Effect: Join/Leave Daily Call based on State
        useEffect(() => {
            if (!callObject) {
                console.log("Daily Effect: callObject not ready.");
                return; // Daily not initialized
            }

            const currentState = callObject.meetingState();
            console.log(`Daily Effect Check: Status='${status}', AI Joined='${AiJoined}', Daily State='${currentState}', URL='${conversationUrl ? 'Set' : 'Not Set'}'`);


            // Join Condition: Status is active, AI has joined (confirmed via WS), URL is available, and not already joined/joining
            if (status === 'active' && conversationUrl && conversationToken && (currentState === 'new' || currentState === 'left-meeting')) {
                console.log('Daily Effect: Conditions met, attempting to join call:', conversationUrl);
                callObject.join({
                    url: conversationUrl,
                    token: conversationToken, // Use the token from Pipecat
                    // Optionally set user name, tokens etc. here if needed
                    // userName: `User_${userId}`
                })
                    .then(() => {
                        console.log('Daily Effect: Join successful.');
                        setDailyJoined(true); // Update local state

                        console.log('muting user')
                        callObject.setLocalAudio(false) //mute local audio until user joins room

                        // callObject.startRecording({ type: "cloud" }); // Consider adding error handling for startRecording

                        // If showing the main UI, ensure devices are set in Daily
                        if (showVideoUI) {
                            console.log("Daily Effect: Setting devices after join...");
                            callObject.setInputDevicesAsync({
                                audioDeviceId: selectedAudioInputId || undefined,
                                videoDeviceId: selectedVideoId || undefined,
                            })
                                .catch(err => console.error("Daily Effect: Error setting input devices", err))

                            if (selectedAudioOutputId && 'setSinkId' in HTMLMediaElement.prototype) {
                                callObject.setOutputDeviceAsync({ outputDeviceId: selectedAudioOutputId })
                                    .catch(err => console.error("Daily Effect: Error setting output device", err));
                            }
                        }
                    })
                    .catch(err => {
                        console.error('Daily Effect: Failed to join call:', err);
                        setInternalErrorMessage(`Daily join error: ${err?.message || err}`);
                        // Decide if this constitutes a full error state
                        // setStatus('error'); // This might trigger leave/cleanup prematurely
                    });
            }
            // Leave Condition: Status indicates ending/completion/error, and currently joined/joining
            else if ((status === 'ending' || status === 'completed' || status === 'error') && (currentState === 'joined-meeting')) {
                console.log(`Daily Effect: Conditions met, leaving call (Status: ${status}, Daily State: ${currentState})`);
                callObject.leave()
                    .then(() => {
                        console.log('Daily Effect: Left meeting successfully.');
                        setDailyJoined(false); // Update local state
                    })
                    .catch(err => console.error('Daily Effect: Error leaving meeting:', err)); // Log error but usually proceed with state change
            }
            // Condition to handle showing main UI *before* joining Daily (e.g., confirming modal)
            else if (showVideoUI && !dailyJoined && (currentState === 'new' || currentState === 'left-meeting')) {
                // If we show the main UI but haven't joined Daily yet (e.g., after confirmation modal)
                // And if the AI has already joined, trigger the join
                if (status === 'active' && AiJoined && conversationUrl) {
                    console.log("Daily Effect: Main UI shown, AI ready, triggering join.");
                    // Join logic is handled by the first `if` condition, this block might be redundant
                    // Or could be used to trigger join explicitly *after* confirmation,
                    // but the main logic should cover it once `showVideoUI` is true *and* `AiJoined` is true.
                    // Ensure the state transitions allow the first `if` block to execute correctly.
                } else {
                    console.log("Daily Effect: Main UI shown, but not ready to join Daily yet.", { status, AiJoined, conversationUrl });
                }
            }


        }, [status, AiJoined, conversationUrl, callObject, showVideoUI, dailyJoined, selectedAudioInputId, selectedVideoId, selectedAudioOutputId, userId]); // Add relevant device IDs if needed for setting after join


        // Function to stop the local media stream used for preview/waiting room
        const stopLocalMediaStream = useCallback(() => {
            console.log("Stopping local media stream (if active)...");
            if (mediaStream) {
                mediaStream.getTracks().forEach((track) => {
                    console.log(`Stopping local track: ${track.kind} (${track.label})`);
                    track.stop();
                });

                setMediaStream(null); // Clear the stream state
            }
            if (videoRef.current) {
                videoRef.current.srcObject = null; // Clear video element source
            }
            // Reset access states
            setCameraAccessGranted(false);
            setMicAccessGranted(false);
        }, [mediaStream]); // Dependency on mediaStream

        // End Conversation Logic
        const endConversation = useCallback(async (reason: string) => {
            console.log(`Action: endConversation triggered. Reason: ${reason}. Current status: ${status}. Test mode: ${isTestInterview}`);

            setStatus('ending'); // Set intermediate state immediately

            // --- Explicit Media Cleanup ---
            stopLocalMediaStream(); // Stop the preview/waiting room stream

            // Stop Daily media tracks explicitly before leaving
            if (callObject) {
                try {
                    console.log("Daily: Stopping camera and microphone...");
                    await callObject.setLocalVideo(false);
                    await callObject.setLocalAudio(false);

                    // Also stop the underlying tracks
                    const localParticipant = callObject.participants()?.local;
                    if (localParticipant?.tracks) {
                        if (localParticipant.tracks.video?.persistentTrack) {
                            localParticipant.tracks.video.persistentTrack.stop();
                        }
                        if (localParticipant.tracks.audio?.persistentTrack) {
                            localParticipant.tracks.audio.persistentTrack.stop();
                        }
                    }
                    console.log("Daily: Camera and microphone stopped.");
                } catch (err) {
                    console.error("Daily: Error stopping media tracks:", err);
                }
            }

            // Stop Daily recording *before* leaving
            if (callObject && status == 'active') {
                try {
                    console.log("Daily: Stopping recording...");
                    if (!isTestInterview) {
                        await callObject.stopRecording();
                        // set a delayed request to call generateCandidateInsights and pass conversationId
                        setTimeout(() => {
                            console.log("Action: Generating candidate insights...");
                            if (isPracticeInterview) {
                                console.log("Practice interview - skipping insights generation");
                                generatePracticeInsights({
                                    "conversation_id": conversationId as string,
                                    "interview_segments": sections.map((section) => ({
                                        "duration": section.duration,
                                        "context": section.instruction,
                                    }))
                                })
                                    .then(() => {
                                        console.log("Action: Successfully Queued Practice Interview Insights.");
                                    })
                                    .catch((err) => {
                                        console.error("Action: Failed to generate practice insights:", err);
                                        setApiErrorMessage("Failed to generate practice insights. Please try again later.");
                                    });
                            }
                            else {
                                console.log("Generating insights for conversation:", conversationId);
                                generateCandidateInsights({
                                    "conversation_id": conversationId as string,
                                    "interview_segments": sections.map((section) => ({
                                        "duration": section.duration,
                                        "context": section.instruction,
                                    }))
                                })
                                    .then(() => {
                                        console.log("Action: Successfully Queued Interview Insights.");
                                    })
                                    .catch((err) => {
                                        console.error("Action: Failed to generate candidate insights:", err);
                                        setApiErrorMessage("Failed to generate candidate insights. Please try again later.");
                                    });
                            }

                        }, 30000) // delay 30 seconds to allow recording to finalize
                        console.log("Daily: Recording stopped.");
                    } else {
                        console.log("Test interview - no recording to stop");
                    }
                } catch (err) {
                    console.error("Daily: Error stopping recording:", err);
                }
                // Leave the Daily call (this also stops Daily's media tracks)
                try {
                    console.log("Daily: Leaving call...");
                    await callObject.leave();
                    console.log("Daily: Left call successfully.");
                    setDailyJoined(false);
                } catch (err) {
                    console.error("Daily: Error leaving call:", err);
                }
            }
            // --- End Explicit Media Cleanup ---

            // Only update backend status if not a test interview
            if (!isTestInterview) {
                if (reason === 'candidate_join_timeout') {
                    console.log("Timer expired, updating status to 'None'.");
                    updateInterviewStatus({ new_status: "Pending Interview", conversation_id: conversationId as string }, interviewId === import.meta.env.VITE_FIRST_MOVE_INTERVIEW_ID ? import.meta.env.VITE_FIRST_MOVE_API : undefined)
                }
                else {
                    console.log("Setting status as Complete.");
                    updateInterviewStatus({ new_status: "Complete", conversation_id: conversationId as string }, interviewId === import.meta.env.VITE_FIRST_MOVE_INTERVIEW_ID ? import.meta.env.VITE_FIRST_MOVE_API : undefined).then(() => {
                        if (onInterviewStatusChange) {
                            onInterviewStatusChange("Complete");
                        }
                        // submit the assessment with answers as conversationId
                        submitAssessment({
                            assessment_id: interviewId,
                            answers: [{ id: "conversation_id", content: conversationId as string }],
                            job_id: job_id as string,
                            submission_type: "Interview"
                        }).then(() => {
                            console.log("Assessment submitted successfully.");
                        }).catch((err) => {
                            console.error("Failed to submit assessment:", err);
                            setApiErrorMessage("Failed to submit your responses. Please try again later.");
                        });
                        onCompletedInterview();
                    })
                }
            }

            // Stop local speech detection if active
            if (speech_status === 'listening' || speech_status === 'initializing') {
                console.log("Manually stopping speech detection during endConversation.");
                stopListening();
            }

            setStatus('completed');
            console.log("Action: Status set to 'completed'.");

            // 4. Delayed state reset (allow time for cleanup effects)
            setTimeout(() => {
                console.log("Cleanup: Resetting non-essential state.");
                // Reset states that should clear for a potential next interview
                setConversationId(null);
                setConversationUrl(null);
                setAiJoined(false);
                setDailyJoined(false);
                setRequestSubmitted(false);
                setRequestType(null);
                setCountdownActive(false);
            }, 500);
        }, [ // Dependencies
            status, conversationId, authToken, pipecatApiUrl, callObject, stopLocalMediaStream, interviewStartTime, userId // Correct dependency
        ]);

        // Effect: Trigger Conversation Creation Once Ready
        useEffect(() => {
            // Create conversation only when:
            // - Not blocked by initial status check
            // - API key and relevant IDs are present
            // - Conversation hasn't been created yet
            // - Status is 'idle' (initial state)
            if (!blockInterview && authToken && agentName && !conversationCreatedRef.current && status === 'idle') {
                console.log("Effect: Triggering conversation creation.");
                createConversation();
            }
        }, [blockInterview, authToken, agentName, status, createConversation, interviewExpired]); // Dependencies

        // Imperative Handle for external control (e.g., parent component ending interview)
        useImperativeHandle(ref, () => ({
            endInterview: (reason: string = 'manual_external_request') => {
                console.log("Imperative Handle: endInterview called with reason:", reason);
                endConversation(reason);
            },
        }), [endConversation]); // Dependency on the memoized endConversation

        // --- Button Logic & UI Helpers ---
        const canStartInterview = cameraAccessGranted && micAccessGranted && AiJoined && dailyJoined; // Requires user media, AI joined, *and* user joined Daily

        const getButtonTooltip = (): string | undefined => {
            if (canStartInterview) return undefined; // No tooltip needed if enabled
            if (blockInterview) return "Interview blocked. Please check status or request retake.";
            if (!cameraAccessGranted || !micAccessGranted) return "Please grant camera and microphone permissions.";
            if (!AiJoined) return "Waiting for the AI interviewer to join the session.";
            if (!dailyJoined) return "Connecting to the video session..."; // Or "Click Join Session"?
            return "Please ensure media permissions are granted and AI has joined."; // Fallback
        };

        // --- Event Handlers ---
        const handleStartInterviewClick = async () => {
            if (!cameraAccessGranted || !micAccessGranted) {
                console.warn("Start clicked but media permissions not granted.");
                setInternalErrorMessage("Please grant camera and microphone access first.");
                return;
            }
            if (blockInterview) {
                console.warn("Start clicked but interview is blocked.");
                // Error message should already be set by checkInterviewStatus
                return;
            }
            if (!AiJoined) {
                console.warn("Start clicked but AI has not joined yet.");
                setInternalErrorMessage("Please wait for the AI interviewer to join.");
                return;
            }
            // Showconfirmation modal
            if (isPracticeInterview) {
                await handleConfirmInterview();
            } else {
                setShowConfirmationModal(true);
            }
        };

        const handleConfirmInterview = async () => {
            setLoadingJoin(true);
            if (callObject) {
                stopListening(); // Stop local speech detection if active
                if (!isTestInterview) {
                    callObject?.startRecording({
                        type: "cloud",
                        layout: {
                            preset: "custom",
                            composition_params: {
                                "mode": "single",
                                "showTextOverlay": false,
                                "showImageOverlay": true,
                                "showBannerOverlay": false,
                                "showWebFrameOverlay": false,
                                "showSidebar": false,
                                "showTitleSlate": false,
                                "enableAutoOpeningSlate": false,
                                "videoSettings.maxCamStreams": 1,
                                "videoSettings.preferScreenshare": false,
                                "videoSettings.omitPausedVideo": false,
                                "videoSettings.omitAudioOnly": false,
                                "videoSettings.omitExtraScreenshares": false,
                                "videoSettings.showParticipantLabels": false,
                                "videoSettings.roundedCorners": false,
                                "videoSettings.cornerRadius_gu": 1.2,
                                "videoSettings.scaleMode": "fit",
                                "videoSettings.scaleModeForScreenshare": "fit",
                                "videoSettings.placeholder.bgColor": "rgb(0, 50, 80)",
                                "videoSettings.highlight.color": "rgb(255, 255, 255)",
                                "videoSettings.highlight.stroke_gu": 0.2,
                                "videoSettings.split.margin_gu": 0,
                                "videoSettings.grid.useDominantForSharing": false,
                                "videoSettings.grid.highlightDominant": true,
                                "videoSettings.grid.preserveAspectRatio": false,
                                "image.assetName": "watermark",
                                "image.position": "top-right",
                                "image.zPosition": "foreground",
                                "image.fullScreen": false,
                                "image.fullScreenScaleMode": "fit",
                                "image.aspectRatio": 1.678,
                                "image.height_gu": 3,
                                "image.margin_gu": 1.7,
                                "image.opacity": 0.9,
                                "image.enableFade": true,
                            },
                            session_assets: {
                                'images/watermark': import.meta.env.VITE_WATERMARK_IMG_URL
                            }
                        }
                    });
                    console.log("Congrats: Starting recording before user joins...");

                    // setLoadingJoin(false);
                } else {
                    // Test interview - not starting recording
                    console.log("Test interview - not starting recording");

                    // Need to send app message with type "conversation.start_test_interview_request"
                    const interaction = {
                        message_type: "conversation",
                        event_type: "conversation.start_test_interview_request", // Custom event for test interviews
                        conversation_id: conversationId
                    };
                    callObject.sendAppMessage(interaction, '*'); // Send to all participants
                    console.log("Congrats: Starting test interview without recording");
                }
                // callObject.updateInputSettings({ audio: { processor: { type: "noise-cancellation" } } });
                // console.log("Congrats2: Started noise cancellation");
                // setLoadingJoin(false);
            }
            setIsRecordingStarting(true);
            // Rest of logic is handles by the on-recording Daily Event
        };


        // Countdown and Retake/Issue Logic
        useEffect(() => {
            // Start countdown timer only when AI joins, user has media, and not already in main UI
            if (AiJoined && canStartInterview && !countdownActive && !showVideoUI && !interviewExpired && !blockInterview) {
                console.log("Starting join countdown timer.");
                setCountdownActive(true);

                //   updateInterviewStatus({ new_status: "Interviewer Ready" })
                //     .then((res) => console.log("Interview status updated on backend:", res))
                //     .catch((err) => console.error("Error updating backend interview status:", err));
            }
        }, [AiJoined, canStartInterview, countdownActive, showVideoUI, interviewExpired, blockInterview]); // Dependencies

        const handleTimeout = () => {
            console.log("Interview join timer expired.");
            if (!showVideoUI) { // Only expire if user hasn't joined the main UI yet
                if (!isTestInterview) {
                    updateInterviewStatus({ new_status: "Pending Interview", conversation_id: conversationId as string }, interviewId === import.meta.env.VITE_FIRST_MOVE_INTERVIEW_ID ? import.meta.env.VITE_FIRST_MOVE_API : undefined)
                }
                setInterviewExpired(true);
                setCountdownActive(false);
                setInternalErrorMessage("You took too long to join the interview. Please request to retake the interview.");
                setStatus('error');
                // Optionally end the conversation immediately if timeout occurs
                endConversation('candidate_join_timeout');
            }
        };

        const handleRetakeRequest = (reason: string) => {
            console.log("Retake request submitted with reason:", reason);
            // TODO: Send request to backend API
            // e.g., api.submitRetakeRequest({ userId, reason, conversationId });
            setRequestSubmitted(true);
            setRequestType("retake");
            setIsRetakeDialogOpen(false);
            // Consider ending the conversation if a retake is requested and session is active
            if (conversationId && status !== 'completed' && status !== 'ending') {
                endConversation('retake_requested');
            }
        };

        const handleIssueSubmit = (issueType: string, details: string) => {
            console.log("Issue reported:", { userId, issueType, details, conversationId });
            setRequestType("issue");
            setIsIssuesDialogOpen(false);
            // Consider ending the conversation if an issue is reported and session is active
            if (conversationId && status !== 'completed' && status !== 'ending') {
                endConversation(`issue_reported:_${issueType}`);
            }

            snackbar.showSuccess("Issue reported to the team. Please retry your interview.");

            if (!onReload) {
                setTimeout(() => {
                    window.location.reload(); // For now, just reload to reset state
                }, 2000)
            } else {
                onReload();
            }
        };

        // Helper to make AI speak (if needed)
        const makeAiSay = (line: string) => {
            if (callObject && conversationId && callObject.meetingState() === 'joined-meeting') {
                console.log("Sending app message to make AI say:", line);
                const interaction = {
                    message_type: "conversation",
                    event_type: "conversation.echo", // Using echo for direct TTS
                    conversation_id: conversationId,
                    properties: {
                        text: line
                    },
                };
                callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)
                console.log("AI 'say' message sent:", interaction);
            } else {
                console.warn("Cannot make AI say: Daily not joined or no conversation ID.", { joined: callObject?.meetingState(), conversationId });
            }
        };

        const endAiCall = () => {
            if (callObject && conversationId && callObject.meetingState() === 'joined-meeting') {
                console.log("Ending a call:");
                const interaction = {
                    message_type: "conversation",
                    event_type: "conversation.end",
                    conversation_id: conversationId,
                    properties: {
                    },
                };
                callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)
                console.log("AI 'end' message sent:", interaction);
                console.log("Setting status as Complete.");
                if (!isTestInterview) {
                    updateInterviewStatus({ new_status: "Complete", conversation_id: conversationId as string }, interviewId === import.meta.env.VITE_FIRST_MOVE_INTERVIEW_ID ? import.meta.env.VITE_FIRST_MOVE_API : undefined).then(() => {
                        if (onInterviewStatusChange) {
                            onInterviewStatusChange("Complete");
                        }
                        // submit the assessment with answers as conversationId
                        submitAssessment({
                            assessment_id: interviewId,
                            answers: [{ id: "conversation_id", content: conversationId as string }],
                            job_id: job_id as string,
                            submission_type: "Interview"
                        }).then(() => {
                            console.log("Assessment submitted successfully.");
                        }).catch((err) => {
                            console.error("Failed to submit assessment:", err);
                            setApiErrorMessage("Failed to submit your responses. Please try again later.");
                        });
                        onCompletedInterview();

                        trackEvent('ai_interview_completed', {
                            job_id: jobid || "",
                            device_type: `Audio: ${audioInputName} | Video: ${videoName}`
                        })
                    });
                }
            } else {
                console.warn("Cannot end AI call: Daily not joined or no conversation ID.", { joined: callObject?.meetingState(), conversationId });
            }
        };

        const updateConversationContext = (context: string) => {
            if (callObject && conversationId) {
                console.log("Updating conversation context:", context);
                const interaction = {
                    message_type: "conversation",
                    event_type: "conversation.overwrite_llm_context", // Using echo for direct TTS
                    conversation_id: conversationId,
                    properties: {
                        context: context
                    },
                };
                callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)
                console.log("Conversation context updated:", interaction);
            } else {
                console.warn("Failed to update context", { joined: callObject?.meetingState(), conversationId });
            }
        };

        // --- Observe Local User Audio Level ---
        const speakingTimerRef = useRef<NodeJS.Timeout | null>(null);

        // Cleanup timer on unmount
        useEffect(() => {
            return () => {
                if (speakingTimerRef.current) {
                    clearTimeout(speakingTimerRef.current);
                }
                if (pauseTimerRef.current) {
                    clearTimeout(pauseTimerRef.current);
                }
            };
        }, []);

        // --- Get Selected Device Names for UI ---
        const getSelectedDeviceNames = () => {
            const audioInput = audioInputDevices.find(d => d.deviceId === selectedAudioInputId);
            const audioOutput = audioOutputDevices.find(d => d.deviceId === selectedAudioOutputId);
            const video = videoDevices.find(d => d.deviceId === selectedVideoId);

            return {
                audioInputName: audioInput?.label || (selectedAudioInputId ? 'Selected Mic' : 'Default Mic'),
                audioOutputName: audioOutput?.label || (selectedAudioOutputId ? 'Selected Speaker' : 'Default Speaker'),
                videoName: video?.label || (selectedVideoId ? 'Selected Camera' : 'Default Camera')
            };
        };

        const { audioInputName, audioOutputName, videoName } = getSelectedDeviceNames();

        // --- Daily Event Handling ---
        const handleAppMessage: DailyAppMessageCallback = useCallback(
            (event: DailyEventObjectAppMessage) => {
                const pipecat_event = event.data as PipecatAppMessageData

                // check if bot has decided to end the call
                if (pipecat_event.type === "conversation.end") {
                    console.log("VideoContainer: Pipecat Interaction Received: conversation.end");
                    // setIsConcluding(true);
                    setIsConversationEnded(true);
                }

                if (pipecat_event.type === "bot-tts-started") {
                    // Bot is streaming text
                    setIsAiSpeaking(true);
                }
                else if (pipecat_event.type === "bot-stopped-speaking") {
                    // Bot has finished speaking
                    setTimeout(() => {
                        setIsAiSpeaking(false)
                    }, 2000)
                }
                else if (pipecat_event.type === "conversation.test_interview_start") {
                    console.log("Received Test start message:", pipecat_event);
                    setShowConfirmationModal(false);

                    if (callObject) {
                        callObject.setLocalAudio(true);

                        // Mark interview start time
                        setInterviewStartTime(Date.now());

                        setShowVideoUI(true); // Show the main VideoContainer UI
                        setIsRecordingStarting(false);

                        // Set initial context (Step 0)
                        console.log("Setting initial interview context (Step 0)");
                        updateConversationContext(interviewContexts[0]);
                        setInterviewStep(0); // Explicitly set step to 0
                        // wait 1 second
                        setTimeout(() => {
                            makeAiSay(`${customGreeting ? customGreeting : "Hey, thanks for joining the call today. Are you ready for your ourteam interview?"}`)
                        }, 1000)
                    }
                }
                const tavusMessage = event.data as TavusAppMessageData;
                if (!tavusMessage || !tavusMessage.event_type) return;

                console.log(
                    `VideoContainer: Tavus Interaction Received: ${tavusMessage.event_type}`,
                    tavusMessage.properties
                );

                switch (tavusMessage.event_type) {
                    case "conversation.utterance":
                        if (tavusMessage.properties?.role === 'user') {
                            console.log("User spoke, resetting pause state");
                            setPauseState(0);
                        }
                        // console.log("Updating last utterance time to:", Date.now());
                        break;
                    case "conversation.replica.started_speaking":
                        console.log("AI started speaking:", tavusMessage.properties);
                        // setLastUtteranceTime(Date.now());
                        // setIsAiSpeaking(true);
                        break;
                    case "conversation.replica.stopped_speaking":
                        // console.log("Updating last utterance time to:", Date.now());
                        // setLastUtteranceTime(Date.now());
                        // setIsAiSpeaking(false);
                        break;
                    default:
                        console.warn(
                            "VideoContainer: Unhandled Tavus Interaction type:",
                            tavusMessage.event_type
                        );
                }
            },
            [conversationStarted, callObject, interviewContexts, customGreeting, isRecordingStarting, conversationId, userName]
        );

        useDailyEvent("app-message", handleAppMessage);

        useDailyEvent("participant-joined", useCallback((event) => {
            console.log("Participant Joined:", event.participant);
            setAiJoined(true);
        }, []));

        useDailyEvent("participant-left", useCallback((event) => {
            console.log("Participant left:", event.participant);
            setIsConcluding(true)
        }, []));

        useDailyEvent("recording-started", useCallback((event) => {
            console.log("Recording started:", event);
            setShowConfirmationModal(false);

            // Create the interview record in backend *before* showing the main UI
            if (conversationId && callObject) {
                console.log("Creating interview record for conversation:", conversationId);
                try {
                    // Start recording now that user has joined
                    console.log("Daily Effect: Starting recording after join...");
                    callObject.setLocalAudio(true);

                    // Mark interview start time
                    setInterviewStartTime(Date.now());

                    // Only proceed if creation was successful
                    setShowVideoUI(true); // Show the main VideoContainer UI
                    setIsRecordingStarting(false);

                    // Set initial context (Step 0)
                    console.log("Setting initial interview context (Step 0)");
                    updateConversationContext(interviewContexts[0]);
                    setInterviewStep(0); // Explicitly set step to 0
                    // wait 1 second
                    setTimeout(() => {
                        makeAiSay(`${customGreeting ? customGreeting : "Hey, thanks for joining the call today. Are you ready for your ourteam interview?"}`)
                    }, 1000)

                } catch (error: any) {
                    console.error("Failed to create interview record:", error);
                    setInternalErrorMessage(`Error starting interview: ${error.message}. Please try again.`);
                }

            } else {
                console.error("Cannot confirm interview: conversationId is missing.");
                setInternalErrorMessage("Cannot start interview: Session ID missing.");
            }
        }, [conversationId, callObject, interviewContexts, userName, isRecordingStarting, customGreeting]));        // Effect to progress interview steps automatically

        useEffect(() => {
            // Create dynamic step durations array from sections prop
            // Index 0 is initial delay (0), then each section's duration
            const stepDurations = [0, ...sections.map(section => section.duration)];
            console.log("Dynamic step durations created from sections:", stepDurations);

            let timeoutId: NodeJS.Timeout | null = null;

            // Only run the timer when the main video UI is shown and the call is active
            if (showVideoUI && callObject && conversationId && callObject.meetingState() === 'joined-meeting') {
                const currentStep = interviewStep;
                const nextStep = currentStep + 1;

                console.log(`Interview step effect running. Current step: ${currentStep}`);

                // Check if there is a next step context
                if (nextStep < interviewContexts.length) {
                    // Get the duration for the *current* step (time until we move to the *next* step)
                    // Use stepDurations[nextStep] because stepDurations[1] is duration for step 0, etc.
                    const durationInSeconds = stepDurations[nextStep];

                    if (durationInSeconds > 0) {
                        console.log(`Scheduling transition from step ${currentStep} to step ${nextStep} in ${durationInSeconds} seconds.`);

                        timeoutId = setTimeout(() => {
                            console.log(`Timer fired: Advancing from step ${currentStep} to step ${nextStep}.`);
                            // Update context to the next step's context
                            updateConversationContext(interviewContexts[nextStep]);
                            // Update the state to reflect the new current step
                            setInterviewStep(nextStep);
                        }, durationInSeconds * 1000);

                    } else {
                        console.log(`Duration for step ${currentStep} is 0 or invalid (${durationInSeconds}s). Not scheduling automatic advance to step ${nextStep}.`);
                        // If duration is 0, you might want to advance immediately or handle differently
                        // For now, it just won't schedule the next step automatically via timer.
                    }
                } else {
                    // We have completed the last step (index interviewContexts.length - 1)
                    // Check the duration associated with the *last* step (stepDurations[interviewContexts.length])
                    const lastStepDuration = stepDurations[interviewContexts.length]; // e.g., stepDurations[6] for 6 contexts (0-5)
                    if (lastStepDuration > 0) {
                        console.log(`Last step (${currentStep}) reached. Scheduling end of interview in ${lastStepDuration} seconds based on its duration.`);
                        timeoutId = setTimeout(() => {
                            console.log("Timer fired after last step's duration. Ending conversation.");
                            // Assuming the last context handles the closing remarks,
                            // we might just end the conversation after its duration.
                            endAiCall()
                        }, lastStepDuration * 1000);
                    } else {
                        console.log(`Last step (${currentStep}) reached. No specific duration to wait before ending, or duration is 0.`);
                        // Consider if you want to end immediately or rely on the last context's flow.
                        // Perhaps the last context itself should trigger the end via an app message?
                    }
                }
            }

            // Cleanup function: Clear the timeout when the effect re-runs or component unmounts
            return () => {
                if (timeoutId) {
                    console.log("Clearing interview step timer.");
                    clearTimeout(timeoutId);
                }
            };            // Dependencies: Re-run when UI shows/hides, call state changes, or the interviewStep state updates.
        }, [showVideoUI, callObject, conversationId, interviewStep, endConversation, sections]);

        // Ref to hold stopListening function for cleanup without causing re-runs
        const stopListeningRef = useRef(stopListening);
        useEffect(() => {
            stopListeningRef.current = stopListening;
        }, [stopListening]);

        // Add this new useEffect for comprehensive media cleanup on unmount
        useEffect(() => {
            // Store video ref at mount time for cleanup (to satisfy React lint rules)
            const currentVideoRefAtMount = videoRef.current;

            // This effect is only for cleanup when component unmounts
            return () => {
                console.log("Component unmounting: Cleaning up ALL media resources");

                // Use refs to get the latest values (not stale closure values)
                const currentMediaStream = mediaStreamRef.current;
                const currentCallObject = callObjectRef.current; // Use ref instead of stale closure
                // Use the ref captured at mount for video element (satisfies lint rule)
                const currentVideoRef = currentVideoRefAtMount || videoRef.current;

                // 1. Stop all tracks from Daily call first (if active)
                if (currentCallObject) {
                    try {
                        const localParticipant = currentCallObject.participants()?.local;
                        if (localParticipant?.tracks) {
                            // Stop video track
                            if (localParticipant.tracks.video?.persistentTrack) {
                                console.log("Stopping Daily video track");
                                localParticipant.tracks.video.persistentTrack.stop();
                            }
                            // Stop audio track
                            if (localParticipant.tracks.audio?.persistentTrack) {
                                console.log("Stopping Daily audio track");
                                localParticipant.tracks.audio.persistentTrack.stop();
                            }
                        }

                        // Stop camera and microphone via Daily API
                        if (currentCallObject.meetingState() === 'joined-meeting') {
                            console.log("Stopping camera and mic via Daily API");
                            currentCallObject.setLocalVideo(false);
                            currentCallObject.setLocalAudio(false);
                        }
                    } catch (err) {
                        console.error("Error stopping Daily tracks:", err);
                    }
                }

                // 2. Stop all tracks in the mediaStream state
                if (currentMediaStream) {
                    console.log("Stopping mediaStream tracks");
                    currentMediaStream.getTracks().forEach(track => {
                        console.log(`Stopping ${track.kind} track: ${track.label}`);
                        track.stop();
                    });
                }

                // 3. Also check for any streams directly attached to video element
                if (currentVideoRef?.srcObject instanceof MediaStream) {
                    console.log("Stopping video element stream tracks");
                    const videoElementStream = currentVideoRef.srcObject as MediaStream;
                    videoElementStream.getTracks().forEach(track => {
                        console.log(`Stopping video element ${track.kind} track: ${track.label}`);
                        track.stop();
                    });
                    currentVideoRef.srcObject = null;
                }

                // 4. Clear Daily call if still active
                if (currentCallObject && currentCallObject.meetingState() !== 'left-meeting') {
                    console.log("Leaving Daily call on unmount");
                    currentCallObject.leave()
                        .then(() => {
                            console.log("Successfully left Daily call, destroying Daily instance");
                            currentCallObject.destroy().catch(err => console.error("Error destroying Daily instance:", err));
                        })
                        .catch(err => {
                            console.error("Error leaving call on unmount:", err);
                            currentCallObject.destroy().catch(err => console.error("Error destroying Daily instance:", err));
                        });
                } else if (currentCallObject) {
                    console.log("Daily call not active, destroying Daily instance");
                    currentCallObject.destroy().catch(err => console.error("Error destroying Daily instance:", err));
                }

                // 5. Stop local speech detection using ref to get latest function
                console.log("Stopping speech detection on unmount");
                if (stopListeningRef.current) {
                    stopListeningRef.current();
                }

                console.log("Component unmount cleanup completed");
            };
        }, []); // Empty dependency - only run on mount/unmount

        // --- Render Logic ---
        return (
            <>
                <DailyAudio
                    ref={audioRef as any} // Type assertion to avoid TS error
                    onPlayFailed={handleAudioPlayFailed}
                    maxSpeakers={3} // Adjust as needed
                />

                {/* Test Mode Banner - show on both screens */}
                {/* <TestModeBanner /> */}
                {showVideoUI ? (
                    <>
                        <VideoContainer
                            status={status}
                            onEndInterview={() => {
                                endConversation('user_request')
                                if (handleEndSession) {
                                    handleEndSession()
                                }
                            }}
                            interviewTitle={interviewName}
                            tavusCallbacks={tavusCallbacks as TavusInteractionCallbacks} // Pass interaction callbacks
                            onCloseDialogSuccessRecord={onCloseModal} // Callback when interview success dialog is closed
                            onIssueReport={() => setShowIssueModal(true)} // Pass issue report handler
                            isInterviewConcluding={isConcluding} // Pass the concluding state
                            isConversationEnded={isConversationEnded} // Pass conversation ended state
                            onConcluding={setIsConcluding} // Pass callback to update concluding state
                            concludingCountdown={countdownSeconds} // Pass the countdown timer value
                            customGreeting={customGreeting} // Pass custom greeting
                            interviewStartTime={interviewStartTime} // Use the stored start time, not current time
                            userId={userId}
                            isTestInterview={isTestInterview} // Pass test mode flag
                            conversationId={conversationId as string}
                            currentCameraId={selectedVideoId}
                            currentMicrophoneId={selectedAudioInputId}
                            aiName={aiName} // Pass the AI interviewer name
                            aiLogoSrc={aiLogoSrc} // Pass the AI logo source
                            interviewLanguage={interviewLanguage}
                            audioStatus={audioStatus}
                        />

                        {/* Test End Button */}
                        {/* <div className="fixed bottom-4 right-4 z-50">
                            <Button
                                onClick={sendTestEndMessage}
                                variant="outline"
                                className="bg-red-500 hover:bg-red-600 text-white border-red-500 hover:border-red-600"
                            >
                                Send Test End
                            </Button>
                        </div> */}
                    </>
                ) : (
                    // waiting room UI
                    <div className="w-full max-w-6xl" id="waiting-room">
                        <div className="bg-yellow-50 sm:hidden flex text-sm dark:bg-yellow-900/20 border border-yellow-200 rounded-lg px-2 py-1 text-yellow-800 dark:text-yellow-300 items-center mb-2">
                            Looks like you’re on your phone—switch to a laptop or desktop for a smoother experience.
                        </div>
                        {/* Browser compatibility warning - moved above the header */}
                        {!isBrowserSupported && (
                            <BrowserCompatibilityWarning
                            />
                        )}
                        <div className="flex flex-row justify-between items-start">
                            <div className="flex flex-col">
                                <h1 className="text-xl sm:text-3xl font-semibold sm:font-bold text-gray-900 dark:text-white mb-2">
                                    {interviewName}
                                </h1>
                                <div className="grid md:grid-cols-[1fr,265px] gap-1 sm:gap-4">
                                    <div className="text-gray-600 dark:text-gray-400 mb-6 flex flex-row items-center gap-2">
                                        <div className="flex-shrink-0">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full">
                                                {formatDuration(maxDuration, "min")}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="md:ml-2 text-sm">
                                                {description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-[1.2fr,1fr] gap-8">
                            {/* Left side - Video preview & Controls */}
                            <div className="flex flex-col gap-2">
                                {/* Video Preview Area */}
                                <div className="rounded-xl border text-card-foreground shadow overflow-hidden h-[400px] bg-gray-100 dark:bg-gray-800 relative ">
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        muted
                                        playsInline
                                        className="w-full h-full object-cover"
                                    />

                                    {/* "You" label with audio visualizer */}
                                    <div className="absolute bottom-3 left-3">
                                        {isTalking && status !== 'connecting' && mediaStream && (
                                            <div
                                                className={`text-sm font-medium text-white bg-black/60 px-3 py-1.5 rounded-lg flex flex-row items-center ${isTalking ? "gap-2" : ""}`}
                                            >
                                                You
                                                <EnhancedAudioVisualizer
                                                    isActive={isTalking}
                                                    variant="wave"
                                                    barCount={4}
                                                    color="green"
                                                />
                                            </div>

                                        )}
                                    </div>

                                    <div className="absolute bottom-3 right-3 sm:hidden block">
                                        <div className="flex flex-row sm:hidden items-center opacity-80 gap-1 bg-black/70 text-white  px-2 py-1.5 rounded-full shadow-sm w-fit">
                                            <ClockIcon className="w-5 h-5" />
                                            <span className="text-xs">{formatDuration(maxDuration)}</span>
                                        </div>
                                    </div>

                                    {/* Loading Indicator */}
                                    {status === 'connecting' && !mediaStream && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 text-white z-10">
                                            <Loader className="h-8 w-8 animate-spin" />
                                            <span className="mt-2 text-sm">Loading video...</span>
                                        </div>
                                    )}

                                    {/* Overlay if Camera Access Denied/Failed */}
                                    {(!cameraAccessGranted || !micAccessGranted) && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 text-white p-4 z-10">
                                            {/* Icon Display */}
                                            <div className="flex flex-row space-x-2 mb-3">
                                                {!cameraAccessGranted && (
                                                    <VideoOffIcon className="h-12 w-12 text-red-400" />
                                                )}
                                                {!micAccessGranted && (
                                                    <MicOffIcon className="h-12 w-12 text-red-400" />
                                                )}
                                            </div>

                                            {/* Message Display */}
                                            <p className="font-semibold mb-1">
                                                {!cameraAccessGranted && !micAccessGranted
                                                    ? "Camera & Microphone Access Needed"
                                                    : !cameraAccessGranted
                                                        ? "Camera Access Needed"
                                                        : "Microphone Access Needed"}
                                            </p>

                                            <p className="text-sm text-center">
                                                Please grant permission in your browser to start the interview.
                                            </p>
                                        </div>
                                    )}

                                    {/* Status indicators */}
                                    <div className="absolute flex flex-row top-3 right-3 z-20 space-x-2">
                                        {/* Mic Status Indicator */}
                                        <div
                                            className={`flex items-center gap-1.5 px-2.5 py-1 opacity-80 rounded-full text-xs font-medium transition-colors backdrop-blur-sm ${micAccessGranted
                                                ? "bg-green-600/70 text-white"
                                                : "bg-red-600/70 text-white"
                                                }`}
                                            title={micAccessGranted ? "Microphone is active" : mediaError?.includes("microphone") ? mediaError : "Microphone access needed or failed"}
                                        >
                                            {micAccessGranted ? (
                                                <MicrophoneIcon className="h-3.5 w-3.5" />
                                            ) : (
                                                <MicOffIcon className="h-3.5 w-3.5" />
                                            )}
                                            <span>{micAccessGranted ? "Mic On" : "Mic Off"}</span>
                                        </div>

                                        {/* Video Status Indicator */}
                                        <div
                                            className={`flex items-center gap-1.5 px-2.5 py-1 opacity-80 rounded-full text-xs font-medium transition-colors backdrop-blur-sm ${cameraAccessGranted
                                                ? "bg-green-600/70 text-white"
                                                : "bg-red-600/70 text-white"
                                                }`}
                                            title={cameraAccessGranted ? "Camera is active" : mediaError?.includes("camera") ? mediaError : "Camera access needed or failed"}
                                        >
                                            {cameraAccessGranted ? (
                                                <VideoIcon className="h-3.5 w-3.5" />
                                            ) : (
                                                <VideoOffIcon className="h-3.5 w-3.5" />
                                            )}
                                            <span>{cameraAccessGranted ? "Camera On" : "Camera Off"}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Device Selectors */}
                                <div className="hidden sm:grid grid-cols-3 gap-2">
                                    {/* Microphone */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild disabled={audioInputDevices.length === 0}>
                                            <div> {/* Wrap for Tooltip */}
                                                <MicrophoneSelector
                                                    label={audioInputName}
                                                    className="text-sm w-full"
                                                />
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-[250px]">
                                            {audioInputDevices.map((device) => (
                                                <DropdownMenuItem
                                                    key={device.deviceId}
                                                    onClick={() => handleMicrophoneSelect(device)}
                                                    className="cursor-pointer text-sm"
                                                    data-selected={selectedAudioInputId === device.deviceId ? 'true' : undefined}
                                                >
                                                    {device.label || `Microphone ${device.deviceId.slice(0, 5)}`}
                                                </DropdownMenuItem>
                                            ))}
                                            {audioInputDevices.length === 0 && <DropdownMenuItem disabled>No microphones found</DropdownMenuItem>}
                                        </DropdownMenuContent>
                                    </DropdownMenu>

                                    {/* Speaker */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild disabled={audioOutputDevices.length === 0}>
                                            <div>
                                                <SpeakerSelector
                                                    label={audioOutputName}
                                                    className="text-sm w-full"
                                                />
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-[250px]">
                                            {audioOutputDevices.map((device) => (
                                                <DropdownMenuItem
                                                    key={device.deviceId}
                                                    onClick={() => handleSpeakerSelect(device)}
                                                    className="cursor-pointer text-sm"
                                                    data-selected={selectedAudioOutputId === device.deviceId ? 'true' : undefined}
                                                    disabled={!('setSinkId' in HTMLMediaElement.prototype)}
                                                >
                                                    {device.label || `Speaker ${device.deviceId.slice(0, 5)}`}
                                                </DropdownMenuItem>
                                            ))}
                                            {audioOutputDevices.length === 0 && (
                                                <DropdownMenuItem disabled>No speakers found</DropdownMenuItem>
                                            )}
                                            {!('setSinkId' in HTMLMediaElement.prototype) && audioOutputDevices.length > 0 && (
                                                <DropdownMenuItem disabled>Speaker selection not supported</DropdownMenuItem>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>

                                    {/* Camera */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild disabled={videoDevices.length === 0}>
                                            <div>
                                                <CameraSelector
                                                    label={videoName}
                                                    className="text-sm w-full"
                                                />
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-[250px]">
                                            {videoDevices.map((device) => (
                                                <DropdownMenuItem
                                                    key={device.deviceId}
                                                    onClick={() => handleCameraSelect(device)}
                                                    className="cursor-pointer text-sm"
                                                    data-selected={selectedVideoId === device.deviceId ? 'true' : undefined}
                                                >
                                                    {device.label || `Camera ${device.deviceId.slice(0, 5)}`}
                                                </DropdownMenuItem>
                                            ))}
                                            {videoDevices.length === 0 && (
                                                <DropdownMenuItem disabled>No cameras found</DropdownMenuItem>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>
                            {/* Right side - Status & Actions */}
                            <div className="flex flex-col gap-6 items-center justify-center w-full mb-24 sm:mb-0">
                                <div className="w-full sm:w-[440px]">
                                    <div className="w-full">
                                        <h2 className="text-2xl md:text-xl font-semibold mt-0 sm:mt-4 md:mt-0 mb-4">
                                            {requestSubmitted ? t("Request Submitted", "Permintaan Dikirim", "Permintaan Dihantar") :
                                                interviewExpired ? t("Your AI interview expired", "Wawancara AI Anda telah kedaluwarsa", "Temuduga AI anda telah tamat tempoh") :
                                                    blockInterview ? t("Access Blocked", "Akses Diblokir", "Akses Disekat") :
                                                        AiJoined ? t("Get ready for your AI interview", "Persiapkan diri Anda untuk wawancara AI", "Sedia untuk temuduga AI anda") :
                                                            (status === 'connecting' || status === 'idle') ? t("Connecting...", "Menghubungkan...", "Menyambung...") :
                                                                t("Get ready for your AI interview", "Persiapkan diri Anda untuk wawancara AI", "Sedia untuk temuduga AI anda")}
                                        </h2>

                                        {!interviewExpired && (
                                            <div className="space-y-4 mb-6">
                                                <div className="flex items-center gap-3">
                                                    <StarIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {t("Start now or come back later", "Mulai sekarang atau kembali lagi nanti", "Mula sekarang atau kembali kemudian")}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <TrendingUpIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {(language === "Demo")
                                                            ? t("Practice freely and get instant feedback", "Latihan dengan bebas dan dapatkan penilaian langsung", "Berlatih dengan bebas dan dapatkan maklum balas segera")
                                                            : t("Stand out beyond a CV and get noticed faster", "Tampilkan potensi terbaik Anda, bukan hanya dari CV", "Tonjolkan diri melebihi CV dan dikenali lebih cepat")}

                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <SettingsIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {t("Check your camera, microphone, and internet connection", "Periksa kamera, mikrofon, dan koneksi internet Anda", "Semak kamera, mikrofon, dan sambungan internet anda")}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <WifiIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {t("Find a quiet place with stable internet", "Temukan tempat yang tenang dengan koneksi internet stabil", "Cari tempat yang tenang dengan internet yang stabil")}
                                                    </span>
                                                </div>
                                                {/* Timer item - moved to last position */}
                                                {cameraAccessGranted && micAccessGranted && AiJoined && countdownActive && (
                                                    <div className="flex items-center justify-between py-2 border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
                                                        <div className="flex items-center gap-3">
                                                            <HourglassIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                                {t("Time to join:", "Waktu untuk bergabung:", "Masa untuk menyertai:")}
                                                            </span>
                                                        </div>
                                                        <div className="text-sm font-mono font-bold text-gray-900 dark:text-white">
                                                            <CountdownTimer
                                                                initialMinutes={timeoutMinutes}
                                                                onTimeout={handleTimeout}
                                                                className="ml-2 inline-block"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="mb-3">
                                            {/* Informational Messages */}
                                            <div className="mb-3 w-full">
                                                {/* Request Submitted Message */}
                                                {requestSubmitted && (
                                                    <div className={`${primaryColorClasses.bgLight} ${primaryColorClasses.bgLightDark} border ${primaryColorClasses.border} ${primaryColorClasses.borderDark} rounded-lg p-3 w-full`}>
                                                        <p className={`text-sm ${primaryColorClasses.text} ${primaryColorClasses.textDark}`}>
                                                            {t(
                                                                `We're reviewing your ${requestType} request and will email you shortly.`,
                                                                `Kami sedang meninjau permintaan ${requestType} Anda dan akan mengirim email segera.`,
                                                                `Kami sedang menyemak permintaan ${requestType} anda dan akan menghantar e-mel tidak lama lagi.`
                                                            )}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Blocked / Expired Messages */}
                                                {!requestSubmitted && (interviewExpired || blockInterview) && (
                                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg p-3 w-full">
                                                        <p className="text-sm text-red-600 dark:text-red-400">
                                                            {blockInterview
                                                                ? (internalErrorMessage || t("Access blocked. Refreshing may end the session.", "Akses diblokir. Memuat ulang dapat mengakhiri sesi.", "Akses disekat. Memuat semula mungkin menamatkan sesi."))
                                                                : <>
                                                                    {t("This interview session has expired.", "Sesi wawancara ini telah kedaluwarsa.", "Sesi temuduga ini telah tamat tempoh.")}
                                                                    <br />
                                                                    {t("You did not join within the allocated time.", "Anda tidak bergabung dalam waktu yang ditentukan.", "Anda tidak menyertai dalam masa yang ditetapkan.")}
                                                                </>}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Waiting States */}
                                                {!requestSubmitted && !interviewExpired && !blockInterview && (
                                                    <>
                                                        {/* Waiting for Permissions */}
                                                        {(!cameraAccessGranted || !micAccessGranted) && (
                                                            <div className="flex items-center justify-center gap-2 text-yellow-600 dark:text-yellow-400">
                                                                <AlertTriangleIcon className="h-4 w-4" />
                                                                <span className="text-sm">{t("Waiting for media permissions...", "Menunggu izin media...", "Menunggu kebenaran media...")}</span>
                                                            </div>
                                                        )}

                                                        {/* Waiting for AI */}
                                                        {cameraAccessGranted && micAccessGranted && !AiJoined && status !== 'connecting' && status !== 'error' && (
                                                            <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
                                                                <Loader className="h-4 w-4 animate-spin" />
                                                                <span className="text-sm">{t("Waiting for AI Interviewer...", "Menunggu Pewawancara AI...", "Menunggu Penemuduga AI...")}</span>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="w-full space-y-3 sm:space-y-2 flex flex-col items-center mt-4 sm:mt-0 gap-2">
                                            {!requestSubmitted ? (
                                                <>
                                                    {/* Retake Button (Only when expired/blocked) */}
                                                    {(interviewExpired || blockInterview) ? (
                                                        <Button
                                                            onClick={() => {
                                                                if (!onReload) {
                                                                    setTimeout(() => {
                                                                        window.location.reload(); // For now, just reload to reset state
                                                                    }, 2000)
                                                                } else {
                                                                    onReload();
                                                                }
                                                            }}
                                                            className={`${primaryColorClasses.bg} ${primaryColorClasses.bgHover} text-white py-6 rounded-full w-full`}
                                                        >
                                                            {t("Retake Interview", "Ulangi Wawancara", "Ulang Temuduga")}
                                                        </Button>
                                                    ) : (
                                                        /* Start Interview Button */
                                                        <Button
                                                            onClick={handleStartInterviewClick}
                                                            disabled={!cameraAccessGranted || !micAccessGranted || !AiJoined || blockInterview || status === 'connecting' || !isBrowserSupported || isLoadingJoin} className={` w-full text-white py-[21px] rounded-full transition-opacity ${(cameraAccessGranted && micAccessGranted && AiJoined && !blockInterview && status !== 'connecting' && isBrowserSupported)
                                                                ? `${primaryColorClasses.bg} ${primaryColorClasses.bgHover} cursor-pointer`
                                                                : "bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-70"
                                                                }`}
                                                            title={getButtonTooltip()}
                                                        >
                                                            {isLoadingJoin
                                                                ? t("Starting Interview...", "Memulai wawancara...", "Memulakan temuduga...")
                                                                : t("Start Interview", "Mulai wawancara", "Mula Temuduga")}

                                                        </Button>
                                                    )}

                                                    {/* Issues Button */}
                                                    {!(interviewExpired || blockInterview || isDisabledButtonIssue) && (
                                                        <button
                                                            onClick={() => setIsIssuesDialogOpen(true)}
                                                            className="w-full text-gray-600 dark:text-gray-400 text-sm border border-gray-300 dark:border-gray-700 rounded-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors mt-2"
                                                        >
                                                            {t("I'm having issues", "Saya mengalami masalah", "Saya mengalami masalah")}
                                                        </button>
                                                    )}

                                                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                                                        {t(
                                                            "ourteam uses generative AI to conduct the AI interview.",
                                                            "ourteam menggunakan AI generatif untuk melakukan wawancara ini.",
                                                            "ourteam menggunakan generatif AI untuk menjalankan temuduga AI ini."
                                                        )}
                                                        <br />
                                                        {t(
                                                            "Your responses are used only to assess your candidacy and are never used to train AI models.",
                                                            "Respons Anda hanya akan digunakan untuk menilai kelayakan sebagai kandidat dan tidak akan digunakan untuk melatih model AI.",
                                                            "Respons anda hanya digunakan untuk menilai kelayakan pencalonan anda dan tidak akan digunakan untuk melatih model AI."
                                                        )}
                                                    </p>
                                                    {isSkipInterview && (
                                                        <div className="flex flex-row justify-center">
                                                            <Button
                                                                variant="outline"
                                                                disabled={isLoadingSkipInterview}
                                                                onClick={onSkipInterview}
                                                                className="w-full flex items-center gap-2 mt-4"
                                                            >
                                                                {isLoadingSkipInterview ? (
                                                                    <>
                                                                        <svg className="animate-spin h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                        Processing
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <ClockIcon className="h-4 w-4" />
                                                                        I want to do this later
                                                                    </>
                                                                )}

                                                            </Button>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <Button
                                                    onClick={onCloseModal}
                                                    variant="outline"
                                                    className="w-full rounded-full"
                                                >
                                                    Close
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div >
                )}
                {
                    showConfirmationModal && (
                        <InterviewConfirmationModal
                            onClose={() => setShowConfirmationModal(false)}
                            onConfirm={handleConfirmInterview}
                            loading={isRecordingStarting}
                            interviewLanguage={interviewLanguage}
                            language={language}
                            companyName={companyInfo?.company_name}
                            colorScheme={isFirstMoveInterview ? 'black' : 'default'}
                        />
                    )
                }

                <RetakeRequestDialog
                    isOpen={isRetakeDialogOpen}
                    onClose={() => setIsRetakeDialogOpen(false)}
                    onSubmit={handleRetakeRequest}
                    userId={userId}
                />

                <IssuesDialog
                    userId={userId}
                    isOpen={isIssuesDialogOpen}
                    onClose={() => setIsIssuesDialogOpen(false)}
                    onSubmit={handleIssueSubmit}
                    colorScheme={isFirstMoveInterview ? 'black' : 'default'}
                />
            </>
        )
    });

// Add display name for better debugging
CustomVoiceInterview.displayName = "CustomVoiceInterview";
export default CustomVoiceInterview;